export const WorkflowTypes = ["launch", "campaign"] as const;
export type WorkflowType = typeof WorkflowTypes[number];

export const StepNames = [
  "brainstorm", "website", "ads", "deploy",
  "create", "content", "highlights", "plan", "keywords", "settings",
  "review", "launch",
  "build"  // Website substep
] as const;
export type StepName = typeof StepNames[number];
export interface Step {
  name: StepName;
  /** Short label for navigation/stepper (e.g., "Landing Page") */
  label: string;
  /** Optional longer title for section headings (e.g., "Landing Page Launch"). Falls back to label if not set. */
  title?: string;
  order: number;
  steps?: Step[];
}

export type Workflow = { steps: Step[] };
export type Workflows = Record<WorkflowType, Workflow>;

export const WorkflowPages = ["brainstorm", "website", "ads", "deploy"] as const;
export type WorkflowPage = typeof WorkflowPages[number];

export const AdsStepNames = ["create", "plan", "launch"] as const;
export type AdsStepName = typeof AdsStepNames[number];

export const AdsSubstepNames = ["content", "highlights", "keywords", "settings", "launch", "review"] as const;
export type AdsSubstepName = typeof AdsSubstepNames[number];

// Website substeps (mirrors ads pattern). Domain selection is now inline on
// the build page, so build is the sole — and terminal — website substep.
// deploy is an action (not a workflow position) reached by explicit user
// action; the workflow ends at /build.
export const WebsiteSubstepNames = ["build"] as const;
export type WebsiteSubstepName = typeof WebsiteSubstepNames[number];

// All substeps across all pages
export const SubstepNames = [...AdsSubstepNames, ...WebsiteSubstepNames] as const;
export type SubstepName = typeof SubstepNames[number];

export const workflows = {
  launch: {
    steps: [
      { name: "brainstorm", label: "Brainstorm", order: 1 },
      { name: "website", label: "Landing Page", title: "Landing Page Designer", order: 2,
        steps: [
          { name: "build", label: "Page Overview", order: 1 }
        ]
      }
    ]
  },
  campaign: {
    steps: [
      {
        name: "create",
        label: "Create",
        order: 1,
        steps: [
          { name: "content", label: "Content", order: 1 },
          { name: "highlights", label: "Highlights", order: 2 }
        ]
      },
      {
        name: "plan",
        label: "Plan",
        order: 2,
        steps: [
          { name: "keywords", label: "Keyword Targeting", order: 1 },
          { name: "settings", label: "Settings", order: 2 }
        ]
      },
      {
        name: "launch",
        label: "Launch",
        order: 3,
        steps: [
          { name: "launch", label: "Campaign Settings", order: 1 },
          { name: "review", label: "Review", order: 2 }
        ]
      }
    ]
  }
} as const satisfies Workflows;

type CampaignSteps = typeof workflows.campaign.steps;
type ExtractTabGroups<T extends readonly any[]> = {
  [K in T[number]["name"]]: Extract<T[number], { name: K }>["steps"] extends readonly { name: infer SN }[]
    ? SN
    : never;
};

export type TabGroupTabNames = ExtractTabGroups<CampaignSteps>;
export type TabGroupName = keyof TabGroupTabNames;

const campaignSteps = workflows.campaign.steps;
export const TabGroups = Object.fromEntries(
  campaignSteps.map((group) => [group.name, group.steps])
) as { [K in TabGroupName]: Extract<CampaignSteps[number], { name: K }>["steps"] };

export function findTabs<T extends TabGroupName>(groupName: T): typeof TabGroups[T] {
  return TabGroups[groupName];
}

const tabGroupNames: readonly TabGroupName[] = ["create", "plan", "launch"] as const;
export function isTabGroupName(name: string | null | undefined): name is TabGroupName {
  return typeof name === "string" && tabGroupNames.includes(name as TabGroupName);
}

const substepToStepMap: Partial<Record<SubstepName, AdsStepName>> = {
  content: "create",
  highlights: "create",
  keywords: "plan",
  settings: "plan",
  launch: "launch",
  review: "launch",
};

export function deriveStep(substep: SubstepName | null | undefined): AdsStepName | null {
  if (!substep) return null;
  return substepToStepMap[substep] ?? null;
}

/**
 * Get the step definition for a given page name.
 *
 * The workflowStore's parseUrl emits a synthetic `page: "ads"` for
 * /campaigns/:uuid/... URLs so UI that keys off `page` keeps working.
 * For "ads" we return a composite view sourced from the campaign workflow's
 * create/plan/launch groups; for website (and future launch pages) we
 * source from the launch workflow.
 */
export function getStepForPage(pageName: WorkflowPage): Step | undefined {
  if (pageName === "ads") {
    return {
      name: "ads",
      label: "Ad Campaign",
      title: "Ad Campaign",
      order: 3,
      steps: workflows.campaign.steps as unknown as Step[],
    } satisfies Step;
  }
  return workflows.launch.steps.find((s) => s.name === pageName);
}

/**
 * Get the ordered list of substep names for a page
 */
export function getSubstepOrder(pageName: WorkflowPage): readonly string[] {
  switch (pageName) {
    case "website":
      return WebsiteSubstepNames;
    case "ads":
      return AdsSubstepNames;
    default:
      return [];
  }
}

/**
 * Determines if a substep is completed based on the current active substep.
 * A substep is considered completed if it comes before the active substep in the order.
 */
export function isSubstepCompleted(
  substepName: string,
  activeSubstep: string | null | undefined,
  substepOrder: readonly string[]
): boolean {
  if (!activeSubstep) return false;

  const currentIndex = substepOrder.indexOf(activeSubstep);
  const substepIndex = substepOrder.indexOf(substepName);

  // If either index is -1 (not found), return false
  if (currentIndex === -1 || substepIndex === -1) return false;

  // A substep is completed if it comes before the current active substep
  return substepIndex < currentIndex;
}

export const workflow = workflows;
